/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Button, Empty, Modal, Typography } from 'antd';
import { FullscreenOutlined, PieChartOutlined } from '@ant-design/icons';
import AnalyticsTable from '../Tables/AnalyticsTable';
import EchartsWrapper from './EchartsWrapper';
import ChartLoader from './ChartLoader';

// Misc imports
import is from '../../utils/is';
import useTheme from '../../utils/hooks/useTheme';
import { formatNumberToThousandSeparator } from '../../utils/string/formatNumberToThousandSeparator';

const UserFeedbackScoreSection = ({ data = {}, sectionTitle = '', isLoading = true, renderOnlyChart = false, renderOnlyTable = false }) => {
  const [chartToFullscreen, setChartToFullscreen] = useState();
  const { t } = useTranslation();
  const { fontSizeLg, fontSizeXxl } = useTheme();

  const getOptions = (totalFeedbacks, negative, positive, percentage) => {
    return {
      title: {
        text: t('Positive') + ' %',
        subtext: `${!isNaN(percentage) ? percentage.toFixed(1) : 0}%`,
        left: 'center',
        top: '165px',
        textStyle: {
          fontSize: fontSizeLg,
          lineHeight: 1.5,
          fontWeight: 'normal',
          color: 'black'
        },
        subtextStyle: {
          fontSize: fontSizeXxl,
          lineHeight: 1,
          color: 'black'
        }
      },
      legend: {
        type: 'scroll',
        show: true,
        data: [t('Negative'), t('Positive')],
        bottom: 0
      },
      tooltip: {
        trigger: 'item',
        formatter: (params) => {
          return `${t('Total Feedbacks')}: ${formatNumberToThousandSeparator(totalFeedbacks)}<br />${params.marker}${params.name}: ${formatNumberToThousandSeparator(params.value)} (${params.percent ? params.percent : 0}%)`;
        },
        confine: true,
        triggerOn: 'mousemove|click',
        enterable: true,
        renderMode: 'html'
      },
      series: [
        {
          name: t('Total Feedbacks'),
          type: 'pie',
          startAngle: 40,
          radius: ['55%', '70%'],
          data: [
            { value: negative, name: t('Negative') },
            { value: positive, name: t('Positive') }
          ],
          label: {
            show: true,
            position: 'outside',
            formatter: (params) => {
              return `${formatNumberToThousandSeparator(params.value)}`;
            },
            labelLine: {
              show: false,
              smooth: true,
              lineStyle: {
                color: '#000000'
              }
            }
          },
          emphasis: {
            label: {
              show: true
            }
          }
        }
      ]
    };
  };

  const tableConfig = {
    descriptionTableData: data,
    isDescriptionTable: true,
    isTableLoading: isLoading,
    noScroll: renderOnlyTable,
    emptyLines: 1,
    descriptionTableFields: [
      {
        label: t('Total Feedbacks'),
        dataField: 'total',
        isPercentage: false,
        isToBeFixed: false
      },
      {
        label: t('Positive'),
        dataField: 'positive',
        isPercentage: false,
        isToBeFixed: false
      },
      {
        label: t('Negative'),
        dataField: 'negative',
        isPercentage: false,
        isToBeFixed: false
      },
      {
        label: t('Positive') + ' %',
        dataField: 'positivePercentage',
        isPercentage: true,
        isToBeFixed: true
      }
    ]
  };

  const chart = (
    <Fragment>
      {
        data.total > 0 ? (
          <EchartsWrapper
            chartOptions={getOptions(data.total, data.negative, data.positive, data.positivePercentage)}
            style={{ height: 367 }}
            loading={isLoading}
          />
        ) : (
          <div className="d-flex content-fill flex-column h-100">
            <div className="mt-5 d-flex align-items-center mb-auto text-center flex-auto align-self-center">
              <Empty
                description={(
                  <Typography.Paragraph>
                    {t('No data available for this chart')}
                  </Typography.Paragraph>
                )}
              />
            </div>
          </div>
        )
      }
    </Fragment>
  );

  return (
    <Fragment>
      {
        renderOnlyChart ? chart : (
          <Fragment>
            {
              !renderOnlyTable ? (
                <Fragment>
                  <div className="mb-6 position-relative">
                    <Typography.Title level={4} className="mb-6">
                      <Typography.Text className="mr-2">{sectionTitle}</Typography.Text>
                      <PieChartOutlined className="ml-2" />
                    </Typography.Title>

                    {isLoading ? <ChartLoader /> : chart}

                    <Button
                      type="text"
                      size="large"
                      disabled={isLoading}
                      icon={<FullscreenOutlined />}
                      className="position-absolute top-0 right-0"
                      onClick={() => setChartToFullscreen(chart)}
                    />
                  </div>

                  <AnalyticsTable
                    descriptionTableData={tableConfig.descriptionTableData}
                    isDescriptionTable={tableConfig.isDescriptionTable}
                    isTableLoading={tableConfig.isTableLoading}
                    emptyLines={tableConfig.emptyLines}
                    descriptionTableFields={tableConfig.descriptionTableFields}
                  />

                  <Modal
                    open={is.truthy(chartToFullscreen)}
                    onCancel={() => setChartToFullscreen()}
                    destroyOnClose
                    footer={null}
                    centered
                    style={{ maxWidth: 1000 }}
                    width={'95%'}
                  >
                    <div className="p-5">
                      {chartToFullscreen}
                    </div>
                  </Modal>
                </Fragment>
              ) : (
                <AnalyticsTable
                  descriptionTableData={tableConfig.descriptionTableData}
                  isDescriptionTable={tableConfig.isDescriptionTable}
                  isTableLoading={tableConfig.isTableLoading}
                  emptyLines={tableConfig.emptyLines}
                  descriptionTableFields={tableConfig.descriptionTableFields}
                />
              )
            }
          </Fragment>
        )
      }
    </Fragment>
  );
};

UserFeedbackScoreSection.propTypes = {
  data: PropTypes.object,
  sectionTitle: PropTypes.string,
  seriesName: PropTypes.string,
  isLoading: PropTypes.bool,
  renderOnlyChart: PropTypes.bool,
  renderOnlyTable: PropTypes.bool
};

export default UserFeedbackScoreSection;
